import axios from "axios";
import { servicesUrlConfig } from "shared/config/setting";
import logger from "shared/utils/logging";

const AUTH_SERVICE_URL = servicesUrlConfig.AUTH_SERVICE_URL;
const AUTH_API = servicesUrlConfig.AUTH_API;

export const userRegister = async (userData) => {
  const url = AUTH_API.register;
  try {
    const response = await axios.post(url, userData);
    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data?.message || "Failed to signup");
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error?.message || error.message;
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Error during userSignup"
    );
  }
};

export const loginForAccessToken = async (username, password) => {
  const url = AUTH_API.login;
  const headers = {
    accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  };
  const data = new URLSearchParams({
    username: username,
    password: password,
  });
  try {
    const response = await axios.post(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(
        `Failed to login for access token: status-code=${
          response.status
        }, message=${response.data?.message || "Unknown error"}`
      );
    }
  } catch (error) {
    // check if connection error
    if (!error.response) {
      throw new Error("Network error: Unable to connect to the server");
    }
    throw new Error(
      error.response?.data?.message ||
        error?.message ||
        "Error during loginForAccessToken"
    );
  }
};

export const getAuthenticatedUserInfo = async (token) => {
  const url = AUTH_API.me;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to fetch current user info"
      );
    }
  } catch (error) {
    logger.error("Error during getCurrentUserInfo:", error);
    throw new Error(
      error.response?.data?.message || "Error during getCurrentUserInfo"
    );
  }
};

export const changePassword = async (token, password, newPassword) => {
  const url = AUTH_API.change_password;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  try {
    const data = {
      old_password: password,
      new_password: newPassword,
    };

    logger.debug("send changePassword request", data);
    const response = await axios.post(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to change user's password"
      );
    }
  } catch (error) {
    logger.error("Error during changePassword:", error);
    throw new Error(
      error.response?.data?.message || "Error during change password"
    );
  }
};

export const getApiKey = async (token) => {
  const url = AUTH_API.api_key;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.data?.message || "Failed to retrieve API key");
    }
  } catch (error) {
    logger.error("Error during getApiKey:", error);
    throw new Error(
      error.response?.data?.message || "Error during getApiKey"
    );
  }
};

export const generateNewApiKey = async (token) => {
  const url = AUTH_API.api_key;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.post(url, {}, { headers });
    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data?.message || "Failed to generate new API key");
    }
  } catch (error) {
    logger.error("Error during generateNewApiKey:", error);
    throw new Error(
      error.response?.data?.message || "Error during generateNewApiKey"
    );
  }
};

export const forgotPassword = async (email) => {
  const url = AUTH_API.forgot_password;
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  try {
    const data = {
      email: email,
    };

    const response = await axios.post(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to send forgot password email"
      );
    }
  } catch (error) {
    logger.error("Error during forgotPassword:", error);
    throw new Error(
      error.response?.data?.message || "Error during forgot password"
    );
  }
};

export const resetPassword = async (email, code, newPassword) => {
  const url = AUTH_API.reset_password;
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  try {
    const data = {
      email: email,
      verification_code: code,
      new_password: newPassword,
    };

    const response = await axios.post(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to reset user's password"
      );
    }
  } catch (error) {
    logger.error("Error during resetPassword:", error);
    throw new Error(
      error.response?.data?.message || "Error during reset password"
    );
  }
};
